/**
 * ADR-167 — the pure logic behind the Region's "Maximum open contracts per
 * person" field on the cluster form, kept out of the component so it can be
 * tested in the node jest environment. Mirrors radar-api's
 * openContractLimit.service effectiveLimit() (D1a + D2).
 */
import { REGION_SYSTEMS_OF_RECORD } from '../constants/discrepancyRadarOnly';

const SYNC_LABELS = {
  [REGION_SYSTEMS_OF_RECORD.ENOVA]: 'Enova',
  [REGION_SYSTEMS_OF_RECORD.AMERICAS]: 'Americas',
};

/**
 * Text field → stored value: '' → null (use the default), an integer >= 1 →
 * that number, anything else → NaN (invalid).
 */
export const parseLimitInput = (input) => {
  if (input === null || input === undefined || String(input).trim() === '') return null;
  const n = Number(String(input).trim());
  return Number.isInteger(n) && n >= 1 ? n : NaN;
};

/** D1a + D2: the limit people in this Region actually get. */
export const effectiveLimit = ({ isRegion, systemOfRecord, value, defaultLimit = 1 }) => {
  if (!isRegion) return 1;
  if (systemOfRecord !== REGION_SYSTEMS_OF_RECORD.RADAR) return 1;
  return value === null || value === undefined ? defaultLimit : value;
};

/** D1a: the field can hold a value above 1 only where radar is the System of Record. */
export const isLimitLocked = ({ isRegion, systemOfRecord }) =>
  isRegion && systemOfRecord !== REGION_SYSTEMS_OF_RECORD.RADAR;

/** D8: the note shown under a locked field. */
export const lockedNote = (systemOfRecord) => (SYNC_LABELS[systemOfRecord]
  ? `Contracts in this Region come from the ${SYNC_LABELS[systemOfRecord]} sync, which handles one contract per person.`
  : 'Set the system of record to Radar to allow concurrent contracts.');

/**
 * What the form sends for the limit, and whether it is a change that needs a
 * reason. `undefined` fields are left out of the payload (the API leaves the
 * column untouched).
 *
 * @returns {{ invalid: boolean, changed: boolean, value: (number|null|undefined) }}
 */
export const limitChange = ({ mode, isRegion, input, initialValue }) => {
  // A cluster that is not (or no longer) a Region can hold no value.
  if (!isRegion) {
    return initialValue !== null && initialValue !== undefined
      ? { invalid: false, changed: true, value: null }
      : { invalid: false, changed: false, value: undefined };
  }
  const parsed = parseLimitInput(input);
  if (Number.isNaN(parsed)) return { invalid: true, changed: false, value: undefined };
  if (mode === 'add') {
    return parsed === null
      ? { invalid: false, changed: false, value: undefined }
      : { invalid: false, changed: true, value: parsed };
  }
  const before = initialValue ?? null;
  return parsed === before
    ? { invalid: false, changed: false, value: undefined }
    : { invalid: false, changed: true, value: parsed };
};

/**
 * D6: does this edit lower the limit people get? Only then is the impact
 * preview fetched (and Save gated on it).
 */
export const isLowering = ({ isRegion, initialSystemOfRecord, systemOfRecord, initialValue, newValue, defaultLimit }) => {
  const before = effectiveLimit({ isRegion, systemOfRecord: initialSystemOfRecord, value: initialValue ?? null, defaultLimit });
  const after = effectiveLimit({ isRegion, systemOfRecord, value: newValue ?? null, defaultLimit });
  return after < before;
};

/** D6: the sentence above the list of people a lowering would leave above. */
export const impactSentence = ({ people = [], newLimit, regionName }) => {
  if (!people.length) return '';
  const who = people.length === 1 ? '1 person' : `${people.length} people`;
  const verb = people.length === 1 ? 'holds' : 'hold';
  return `Can't lower to ${newLimit}: ${who} in ${regionName || 'this Region'} ${verb} more than ${newLimit} open contract${newLimit === 1 ? '' : 's'}.`;
};
